import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ChatConversations,
  ConversationsHeader,
} from "./components/chat/ChatConversations";
import { ChatHeader } from "./components/chat/ChatHeader";
import { ChatInput } from "./components/chat/ChatInput";
import { VoiceCallBar, VoiceRing } from "./components/chat/VoiceMode";
import { ChatLayout } from "./components/chat/ChatLayout";
import { ChatMessages } from "./components/chat/ChatMessages";
import { ChatEmptyState } from "./components/chat/ChatEmptyState";
import {
  ConfirmCustomerInfo,
  ViewCalendarEvent,
  ClientInfoDialog,
  LoginDialog,
} from "./components/dialogs";
import { useChat } from "./hooks/use-chat";
import {
  usePublicConversations,
  useStartNewConversation,
} from "./hooks/use-public-conversations";
import { useMessageNotifications } from "./hooks/use-message-notifications";
import { useCustomerFlow } from "./hooks/use-customer-flow";
import { type BookingEvent } from "./hooks/use-public-booking";
import {
  usePublicConnectorInfo,
  usePublicSessionInfo,
} from "./hooks/use-public-session-info";
import { useSessionHandle } from "./hooks/use-session-handle";
import { usePublicWebSockets } from "./hooks/use-websockets";
import {
  type PhoneHandoff,
  PhoneHandoffOverlay,
} from "./components/chat/PhoneHandoffOverlay";
import {
  activeBookingCalendar,
  BookingCalendarPanel,
} from "./components/booking/BookingCalendar";
import { useWebAudio } from "./hooks/use-web-audio";
import { useEmbedIntent } from "./hooks/use-embed-intent";
import { reportCallState, widgetOptionsSearch } from "./lib/embed";
import { useDocumentBranding } from "./components/chat/branding";
import { usePublicTranslation } from "@/features/utils/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export function PublicChat() {
  const { sessionPublicId } = useParams<{ sessionPublicId: string }>();
  const { t } = usePublicTranslation();

  // `/c/new` is the widget the embed opens on, and it is a real chat: branding, agent,
  // composer, all of it. It used to create a session here and redirect; nothing is
  // created now until somebody says something (see use-session-handle.ts).
  if (sessionPublicId === "new") {
    return <PublicChatPage />;
  }

  if ((sessionPublicId?.length || 0) < 6) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">{t("chat.invalid")}</div>
      </div>
    );
  }

  return <PublicChatPage />;
}

const PublicChatPage = () => {
  const { sessionPublicId: routeId } = useParams<{ sessionPublicId: string }>();
  const { t } = usePublicTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  usePublicWebSockets();

  // The conversation, if there is one yet — and the way to bring one into being.
  const { publicId: sessionPublicId, ensureSession } =
    useSessionHandle(routeId);
  const pending = !sessionPublicId;

  const {
    session,
    isLoading,
    allMessages,
    handleSendText,
    sendText,
    openBookingCalendar,
    textInput,
    setTextInput,
    messagesEndRef,
    lastMessageRef,
    sendMessage,
    authRequired,
    setAuthRequired,
    isStreaming,
  } = useChat(sessionPublicId, ensureSession);

  // Before a conversation exists the chat still has to draw itself, and everything it
  // draws — branding, the agent's name, the call button — belongs to the connector.
  const { data: sessionOwnInfo } = usePublicSessionInfo(sessionPublicId);
  const { data: connectorInfo } = usePublicConnectorInfo(
    pending ? params.get("connector") || undefined : undefined,
  );
  const sessionInfo = sessionOwnInfo || connectorInfo;

  // "Continuer par téléphone": while the agent calls, the chat steps aside behind the
  // "appel en cours" screen, which closes once the code read out signs the chat in.
  const [phoneHandoff, setPhoneHandoff] = useState<PhoneHandoff | null>(null);

  // The booking calendar takes the whole chat while the visitor is in it, like the
  // "appel en cours" screen. The one the agent just opened comes up by itself, once:
  // a visitor who closed it gets it back from its card in the thread, not on every
  // render.
  const [openCalendar, setOpenCalendar] = useState<string | null>(null);
  const shownCalendars = useRef<Set<string>>(new Set());

  // New-message notifications: sound, tab-title unread count, and relay to the
  // embed launcher (badge + auto-reopen).
  useMessageNotifications(sessionPublicId, allMessages);

  const activeCalendar = activeBookingCalendar(allMessages);
  useEffect(() => {
    if (!activeCalendar || shownCalendars.current.has(activeCalendar)) return;
    shownCalendars.current.add(activeCalendar);
    setOpenCalendar(activeCalendar);
  }, [activeCalendar]);

  const branding = sessionInfo?.branding;
  // On `<html>` too, so what is portalled out of the panel wears it (see the hook).
  useDocumentBranding(branding, params.get("theme"));
  // Branding logo (primary-action logo) overrides the company logo for the avatar.
  const avatarLogo = branding?.logo || sessionInfo?.client?.logo;

  // Web audio (mic) — switch this conversation to voice and back.
  const {
    status: audioStatus,
    error: audioError,
    start: startAudio,
    stop: stopAudio,
    sendText: sendAudioText,
    agentAnalyserRef,
    micAnalyserRef,
    phaseRef,
    isThinking: agentIsThinking,
  } = useWebAudio(
    sessionPublicId ? `/api/sessions/v1/public/${sessionPublicId}` : undefined,
  );
  /**
   * Ask for the call — from the phone button, or from the host page.
   *
   * It cannot just dial: picking up is the other thing that creates a conversation (the
   * audio grant is minted against a session), and `basePath` above only follows on the
   * re-render the creation triggers. So asking and dialling are two steps, and the
   * effect below is the one that dials, once there is something to dial on.
   */
  const [audioAsks, setAudioAsks] = useState(0);
  const askForAudio = useCallback(() => {
    setAudioAsks((n) => n + 1);
    void ensureSession();
  }, [ensureSession]);
  const onCall = audioStatus === "live" || audioStatus === "connecting";
  useEffect(() => {
    if (audioError) toast.error(audioError);
  }, [audioError]);

  // What the host page asked for through `HeyUpChat({ … })`, or the `data-*` defaults
  // embed.js baked into the iframe URL.
  const {
    otpPhone,
    clearOtpPhone,
    audioRequest,
    hangupRequest,
    pendingMessage,
    clearPendingMessage,
    draft,
    clearDraft,
  } = useEmbedIntent(sessionPublicId);

  // A first question the host put behind its own button (`HeyUpChat({ message })`),
  // or its booking calendar (`{ booking: true }`). Sent once the thread is loaded, so
  // it lands in the conversation it belongs to rather than in whichever one finishes
  // loading.
  const sentMessageRef = useRef(false);
  useEffect(() => {
    if (!pendingMessage || sentMessageRef.current) return;
    // With no conversation yet there is nothing to wait for and nothing to talk over —
    // and this message is what creates one. With one, wait for it to load first.
    if (!pending) {
      if (isLoading || !session) return;
      // A message that rode in on the URL belongs to the conversation being *opened*.
      // The widget resumes a thread for 12h, so replaying it on a thread the visitor
      // has already spoken in would talk over them on every page view.
      if (
        pendingMessage.fromUrl &&
        allMessages.some((m) => m.type === "user")
      ) {
        clearPendingMessage();
        return;
      }
    }
    sentMessageRef.current = true;
    void (
      pendingMessage.booking
        ? openBookingCalendar(pendingMessage.text)
        : sendText(pendingMessage.text)
    ).finally(() => {
      sentMessageRef.current = false;
      clearPendingMessage();
    });
  }, [
    pendingMessage,
    pending,
    isLoading,
    session,
    allMessages,
    sendText,
    openBookingCalendar,
    clearPendingMessage,
  ]);

  // …and the softer half: written into the box for the visitor to send themselves.
  useEffect(() => {
    if (!draft) return;
    setTextInput(draft);
    clearDraft();
  }, [draft, setTextInput, clearDraft]);

  // The host page asking is the same ask as the button.
  const handledEmbedAudioRef = useRef(0);
  useEffect(() => {
    if (audioRequest <= handledEmbedAudioRef.current) return;
    handledEmbedAudioRef.current = audioRequest;
    askForAudio();
  }, [audioRequest, askForAudio]);

  // The one place that dials. Each ask is honoured once, by its number: `startAudio` is
  // rebuilt whenever the status changes, so an effect keyed on the callback alone would
  // re-dial the moment the visitor hung up.
  const handledAudioRef = useRef(0);
  useEffect(() => {
    if (audioAsks <= handledAudioRef.current) return;
    // Two things are waited for rather than decided against: `webCallEnabled` arrives
    // with the info, and the session may still be being created by the ask itself.
    if (!sessionInfo || !sessionPublicId) return;
    handledAudioRef.current = audioAsks;
    if (!sessionInfo.webCallEnabled) {
      // The tenant has not opened "call here" on this connector. Silent for the
      // visitor — the conversation is there, it just stays written.
      console.warn("[HeyUp] web call is disabled on this connector");
      return;
    }
    void startAudio();
  }, [audioAsks, sessionInfo, sessionPublicId, startAudio]);

  const handledHangupRef = useRef(0);
  useEffect(() => {
    if (hangupRequest <= handledHangupRef.current) return;
    handledHangupRef.current = hangupRequest;
    void stopAudio();
  }, [hangupRequest, stopAudio]);

  // Relay the voice leg's state so the host can read it back in `HeyUpChat.state()`.
  useEffect(() => {
    reportCallState(audioStatus);
  }, [audioStatus]);

  const {
    showCustomerDialog,
    showEventDialog: showAutoEventDialog,
    selectedEvent: autoSelectedEvent,
    handleCustomerDialogClose,
    handleEventDialogClose: handleAutoEventDialogClose,
    events,
  } = useCustomerFlow(sessionPublicId);

  // Local state for manually opened event (from attachments)
  const [manualSelectedEvent, setManualSelectedEvent] =
    useState<BookingEvent | null>(null);
  const [showManualEventDialog, setShowManualEventDialog] = useState(false);

  // Client info dialog state
  const [showClientInfoDialog, setShowClientInfoDialog] = useState(false);

  // The panel shows one thing at a time: the conversation, or the list of them.
  const [showConversations, setShowConversations] = useState(false);
  const { data: conversations = [], isLoading: isLoadingConversations } =
    usePublicConversations(sessionPublicId!, showConversations);
  const startNewConversation = useStartNewConversation();

  // Keep the embed's *options* (?sound=0) across a thread switch — they are set once,
  // at iframe load, and hold for the whole widget. What the host asked for on the way
  // in (start on audio, this visitor's phone) does not travel: it was spent on the
  // conversation it opened, and carrying it would re-dial and re-text on every thread.
  const goToConversation = (publicId: string) => {
    setShowConversations(false);
    if (publicId === sessionPublicId) return;
    navigate(`/c/${publicId}${widgetOptionsSearch(location.search)}`);
  };

  const handleNewConversation = async () => {
    if (startNewConversation.isPending) return;
    try {
      const { public_id } = await startNewConversation.mutateAsync(
        sessionPublicId!,
      );
      goToConversation(public_id);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("chat.new_conversation_failed"),
      );
    }
  };

  const handleEventClick = (event: BookingEvent) => {
    setManualSelectedEvent(event);
    setShowManualEventDialog(true);
  };

  // A calendar that gave way to the appointment the visitor already has: that one is shown
  // instead, the way any appointment of theirs is. Read again only when the list in hand
  // does not hold it — after the code, the visitor's bookings are another customer's.
  const queryClient = useQueryClient();
  const showExistingBooking = async (eventId?: string) => {
    setOpenCalendar(null);
    const upcoming = (list: BookingEvent[] = []) =>
      list.filter((e) => e.start_time > Date.now() && e.status !== "cancelled");
    const pick = (list: BookingEvent[]) =>
      (eventId ? list.find((e) => e.id === eventId) : undefined) ||
      (eventId ? undefined : list[0]);
    let event = pick(upcoming(events));
    if (!event) {
      const key = ["public-booking", sessionPublicId];
      await queryClient.invalidateQueries({ queryKey: key });
      const fresh = upcoming(queryClient.getQueryData<BookingEvent[]>(key));
      event = pick(fresh) || fresh[0];
    }
    if (event) handleEventClick(event);
  };

  const handleManualEventDialogClose = (open: boolean) => {
    setShowManualEventDialog(open);
    if (!open) {
      setManualSelectedEvent(null);
    }
  };

  // Determine which dialog to show (auto flow takes precedence)
  const showEventDialog = showAutoEventDialog || showManualEventDialog;
  const selectedEvent = autoSelectedEvent || manualSelectedEvent;
  const handleEventDialogClose = showAutoEventDialog
    ? handleAutoEventDialogClose
    : handleManualEventDialogClose;

  // Switching threads (a pick from the list, a new question, a transfer) loads a
  // session we have no cache for. Keep the panel — its header, its card, its branding
  // — rather than blanking the widget to a bare full-screen line.
  //
  // `pending` is not that case: there is no conversation to wait for, and the widget is
  // fully usable — an empty thread with a live composer, which is exactly what it looks
  // like before the first message either way.
  if (!pending && (isLoading || !session)) {
    return (
      <ChatLayout
        branding={branding}
        header={<ChatHeader sessionInfo={sessionInfo} />}
        messages={
          <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
            {isLoading ? t("chat.loading") : t("chat.not_found")}
          </div>
        }
      />
    );
  }

  if (showConversations) {
    return (
      <ChatLayout
        branding={branding}
        header={<ConversationsHeader />}
        messages={
          <ChatConversations
            conversations={conversations}
            isLoading={isLoadingConversations}
            onSelect={goToConversation}
            onNewConversation={handleNewConversation}
            isStartingConversation={startNewConversation.isPending}
          />
        }
      />
    );
  }

  return (
    <>
      <ChatLayout
        branding={branding}
        header={
          <ChatHeader
            sessionInfo={sessionInfo}
            onInfoClick={() => setShowClientInfoDialog(true)}
            // Not while on the phone: leaving the thread would mean leaving the call.
            onConversationsClick={
              onCall || pending ? undefined : () => setShowConversations(true)
            }
          />
        }
        messages={
          // On a call the real thread stays put: the ring floats over it and its
          // background fades out downward, so the last turns — hers and the visitor's,
          // with the usual bubbles and the typing indicator — stay readable underneath.
          <div className="relative flex min-h-0 flex-1 flex-col">
            <ChatMessages
              allMessages={allMessages}
              messagesEndRef={messagesEndRef}
              lastMessageRef={lastMessageRef}
              events={events}
              onEventClick={handleEventClick}
              session={
                session && sessionInfo?.agents
                  ? { ...session, agents: sessionInfo.agents }
                  : session
              }
              showAgentAvatar
              agentName={sessionInfo?.agent?.name}
              agentLogo={avatarLogo}
              isTyping={isStreaming || (onCall && agentIsThinking)}
              topSpacerClass={onCall ? "h-52" : undefined}
              bookingCalendar={
                sessionPublicId ? { onOpen: setOpenCalendar } : undefined
              }
              emptyState={
                <ChatEmptyState
                  agentName={sessionInfo?.agent?.name}
                  clientName={sessionInfo?.client?.name}
                  clientLogo={avatarLogo}
                />
              }
            />
            {openCalendar && sessionPublicId && !phoneHandoff && (
              <BookingCalendarPanel
                key={openCalendar}
                publicId={sessionPublicId}
                callId={openCalendar}
                onClose={() => setOpenCalendar(null)}
                onExisting={(eventId) => void showExistingBooking(eventId)}
              />
            )}
            {phoneHandoff && sessionPublicId && (
              <PhoneHandoffOverlay
                publicId={sessionPublicId}
                handoff={phoneHandoff}
                onClose={() => setPhoneHandoff(null)}
              />
            )}
            {onCall && (
              <VoiceRing
                agentAnalyserRef={agentAnalyserRef}
                micAnalyserRef={micAnalyserRef}
                phaseRef={phaseRef}
              />
            )}
          </div>
        }
        input={
          phoneHandoff || openCalendar ? undefined : onCall ? (
            <VoiceCallBar
              onHangup={stopAudio}
              connecting={audioStatus === "connecting"}
              onSendText={sendAudioText}
            />
          ) : (
            <ChatInput
              textInput={textInput}
              setTextInput={setTextInput}
              onSubmit={handleSendText}
              disabled={sendMessage.isPending}
              agentName={sessionInfo?.agent?.name}
              phoneNumber={sessionInfo?.phoneNumber}
              hidePoweredBy={branding?.hide_powered_by ?? false}
              webCallEnabled={sessionInfo?.webCallEnabled}
              onStartAudio={askForAudio}
            />
          )
        }
      />

      {/* All four act on a conversation — its customer, its bookings, its identity
          gate. Before there is one they have nothing to act on, and rendering them
          would point their requests at `/public/undefined/...`. */}
      {sessionPublicId && (
        <>
          <ClientInfoDialog
            publicId={sessionPublicId}
            open={showClientInfoDialog}
            onOpenChange={setShowClientInfoDialog}
            sessionInfo={sessionInfo}
            onPhoneHandoff={setPhoneHandoff}
            events={events}
            onEventClick={handleEventClick}
          />

          <ConfirmCustomerInfo
            publicId={sessionPublicId}
            open={showCustomerDialog}
            onOpenChange={handleCustomerDialogClose}
          />

          <ViewCalendarEvent
            publicId={sessionPublicId}
            event={selectedEvent}
            open={showEventDialog}
            onOpenChange={handleEventDialogClose}
          />

          {/* Two ways in, one dialog. The LLM called a tool that needs a verified
          identity → resume the blocked action once the code checks out. Or the host
          page handed us a phone nobody proved (`HeyUpChat({ customer })`) → the code is
          already on its way when it opens. */}
          <LoginDialog
            publicId={sessionPublicId}
            open={authRequired || !!otpPhone}
            onOpenChange={(open) => {
              setAuthRequired(open);
              if (!open) clearOtpPhone();
            }}
            resume={authRequired}
            initialPhone={otpPhone || undefined}
            autoSend={!!otpPhone && !authRequired}
            phoneHandoff={!!sessionInfo?.phoneHandoffEnabled}
            onCallStarted={(handoff) => {
              clearOtpPhone();
              setPhoneHandoff(handoff);
            }}
            onSuccess={clearOtpPhone}
          />
        </>
      )}
    </>
  );
};
